import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';
import { parseUrlParam } from '~/components/Filter';
import { DeviceArch, DeviceKind, DeviceService, DeviceStatus } from '~/generated/graphql';

const ARCH_SCHEMA = z.array(z.enum([DeviceArch.Arm, DeviceArch.Arm64, DeviceArch.X64]));
const DEMO_SCHEMA = z.array(z.boolean());
const DISPLAY_ON_SCHEMA = z.array(z.boolean());
const DISPLAY_SERVER_SCHEMA = z.array(z.string());
const GROUP_SCHEMA = z.array(z.number());
const INTERNAL_SCHEMA = z.array(z.boolean());
const KIND_SCHEMA = z.array(
  z.enum([
    DeviceKind.Android,
    DeviceKind.Brightsign,
    DeviceKind.Linux,
    DeviceKind.Samsung,
    DeviceKind.Webos,
  ]),
);
const MODEL_SCHEMA = z.array(z.string());
const PLAYER_VERSION_SCHEMA = z.array(z.string());
const SERVICE_SCHEMA = z.array(z.nativeEnum(DeviceService));
const SILENCE_SCHEMA = z.array(z.boolean());
const STATUS_SCHEMA = z.array(z.nativeEnum(DeviceStatus));
const TAG_SCHEMA = z.array(z.string());
const TV_BRAND_SCHEMA = z.array(z.number());

const parseScalarNumber = (searchParams: URLSearchParams, key: string): number | null => {
  const raw = searchParams.get(key);
  if (raw == null) return null;
  const num = Number(raw);
  return isNaN(num) ? null : num;
};

export const getDeviceFiltersParams = (searchParams: URLSearchParams) => ({
  arch: parseUrlParam(
    () => ARCH_SCHEMA.parse(JSON.parse(searchParams.get('arch') || '[]')),
    'arch',
  ),
  demo: parseUrlParam(
    () => DEMO_SCHEMA.parse(JSON.parse(searchParams.get('demo') || '[]')),
    'demo',
  ),
  displayOn: parseUrlParam(
    () => DISPLAY_ON_SCHEMA.parse(JSON.parse(searchParams.get('displayOn') || '[]')),
    'displayOn',
  ),
  displayServers: parseUrlParam(
    () => DISPLAY_SERVER_SCHEMA.parse(JSON.parse(searchParams.get('displayServers') || '[]')),
    'displayServers',
  ),
  groups: parseUrlParam(
    () => GROUP_SCHEMA.parse(JSON.parse(searchParams.get('groups') || '[]')),
    'groups',
  ),
  internal: parseUrlParam(
    () => INTERNAL_SCHEMA.parse(JSON.parse(searchParams.get('internal') || '[]')),
    'internal',
  ),
  kind: parseUrlParam(
    () => KIND_SCHEMA.parse(JSON.parse(searchParams.get('kind') || '[]')),
    'kind',
  ),
  models: parseUrlParam(
    () => MODEL_SCHEMA.parse(JSON.parse(searchParams.get('models') || '[]')),
    'models',
  ),
  playerVersions: parseUrlParam(
    () => PLAYER_VERSION_SCHEMA.parse(JSON.parse(searchParams.get('playerVersions') || '[]')),
    'playerVersions',
  ),
  services: parseUrlParam(
    () => SERVICE_SCHEMA.parse(JSON.parse(searchParams.get('services') || '[]')),
    'services',
  ),
  silence: parseUrlParam(
    () => SILENCE_SCHEMA.parse(JSON.parse(searchParams.get('silence') || '[]')),
    'silence',
  ),
  status: parseUrlParam(
    () => STATUS_SCHEMA.parse(JSON.parse(searchParams.get('status') || '[]')),
    'status',
  ),
  storageBelow: parseScalarNumber(searchParams, 'storageBelow'),
  tags: parseUrlParam(() => TAG_SCHEMA.parse(JSON.parse(searchParams.get('tags') || '[]')), 'tags'),
  temperatureAbove: parseScalarNumber(searchParams, 'temperatureAbove'),
  tvBrands: parseUrlParam(
    () => TV_BRAND_SCHEMA.parse(JSON.parse(searchParams.get('tvBrands') || '[]')),
    'tvBrands',
  ),
  uptimeBelow: parseScalarNumber(searchParams, 'uptimeBelow'),
});

export const useDeviceFiltersParams = () => {
  const [searchParams] = useSearchParams();
  const [_, setSavedParamsString] = useLocalStorage('devices:filters', '');

  // done here because we do not have control over all search params (page, view, etc)
  useEffect(() => {
    setSavedParamsString(() => searchParams.toString());
  }, [searchParams, setSavedParamsString]);

  return getDeviceFiltersParams(searchParams);
};
